// ─────────────────────────────────────────────────────────────────────────────
// 🔑 WHAT STEPS 6 AND 7 SHOW, DECIDED FROM THE SERVER'S STATE (2026-10-16).
//
// Pure functions so a test can call them, and so the screens derive Continue and the step-7
// phase only from what the server sent (constants/guidedStepRules on the backend decides it).
// ─────────────────────────────────────────────────────────────────────────────

/** Step 6's Continue: verified by Doorly AND step 6 complete by the server's rule. */
export function step6CanContinue(payload) {
  if (!payload) return false;
  // 🔑 SELF-DRIVEN (Jesse, 2026-10-26): the figures are ready once the step-5 check is complete (server: figuresReady) —
  // never staff acceptance. Continue = those figures confirmed (income AND assets) with no dispute open.
  const ready = payload.figuresReady != null ? Boolean(payload.figuresReady) : Boolean(payload.verified && payload.verified.ready);
  return ready && Boolean(payload.step6 && payload.step6.complete);
}

/**
 * Step 7's phase. 🚨 THE AUTHORIZATION IS NEVER SHOWN WHILE STEP 6 IS INCOMPLETE — including any
 * open income or assets dispute — nor while a co-borrower invite is pending. Reaching step 7 by
 * any route (a resumed session, the menu, a deep link) lands here, so this is the guard.
 * @returns {'blocked'|'declined'|'invite_pending'|'joint_intent'|'partner'|'reuse'|'conditions'|'waiting'|'pulling'|'authorize'}
 */
// 🔑 STEP-7 WORK: a credit need routed here, OR one of the borrower's own open letters of explanation (Jesse,
// 2026-10-22 — the pull's letters had no guided screen), OR (2026-10-28) a credit-condition document not yet sent or an
// open file condition — they moved here from Documents, which keeps no upload.
export const hasStep7Work = (d, thisStep = 7) => (d && (d.needs || []).some((n) => Number(n.step) === thisStep))
  || (d && Array.isArray(d.letters) && d.letters.length > 0)
  || (d && Array.isArray(d.documentConditions) && d.documentConditions.some((c) => !c.answered))
  || (d && Array.isArray(d.fileConditions) && d.fileConditions.length > 0)
  // 🔑 automatic LOX Phase 1: the session's own open letter asks (backend utils/loxAsks — GET /step7 letterAsks).
  || (d && d.letterAsks && Number(d.letterAsks.open) > 0);

export function step7Phase(d, thisStep = 7) {
  if (!d) return 'blocked';
  // 🔑 WHO DECLINED (walkthrough item 7, Jesse 2026-10-25): the borrower who did authorize is not offered "Authorize it
  // now instead" — their own screen says the other borrower has not authorized it.
  if (d.joint && d.joint.reason === 'declined') return d.joint.declinedByMe === false ? 'other_declined' : 'declined';
  // 🔑 Reopened for a letter (automatic LOX; gate 'letters_open'): submitted, so never the authorization.
  if (d.letterAsks && d.letterAsks.reopened) return 'conditions';
  if (!d.step6Ready) return 'blocked';
  if (d.step6 && Array.isArray(d.step6.openDisputes) && d.step6.openDisputes.length) return 'blocked';
  if (d.pendingInvite) return 'invite_pending';
  if (d.jointIntent && d.jointIntent.required && !d.jointIntent.affirmed) return 'joint_intent';
  // 🚨 A JOINT FILE WAITS FOR EVERYONE BEFORE ANY REPORT IS USED (gating audit G8, 2026-10-16). The reuse
  // branch below came first, so a joint borrower whose own report was inside the window went straight to
  // /submit without the other borrower's consent; only /submit's handshake stood in the way.
  // 🔑 HIS OWN AUTHORIZATION WAS USED BY THE JOINT PULL (Jesse, 2026-10-22): he is never asked a second time. The
  // server ran his submit on it (backend utils/jointSubmit); the screen goes on — letters first if any.
  if (d.joint && d.myAuthorization && d.myAuthorization.usedByPull && !d.myAuthorization.fresh) {
    return hasStep7Work(d, thisStep) ? 'conditions' : 'pulled';
  }
  if (d.joint && !d.joint.ready) return d.myAuthorization && d.myAuthorization.fresh ? 'waiting' : 'authorize';
  if (d.partnerCredit && !(d.report && d.report.action === 'reuse')) return 'partner';
  // 🔑 A CLASSIC PULL REUSED IN A FRESH APPLICATION (backend utils/classicFreshStart, Jesse 2026-10-29): the borrower
  // authorizes first (a fresh consent, no new inquiry); then the reuse screen below.
  if (d.consentBeforeReuse) return 'authorize';
  if (d.report && d.report.action === 'reuse') {
    return hasStep7Work(d, thisStep) ? 'conditions' : 'reuse';
  }
  if (d.myAuthorization && d.myAuthorization.fresh) {
    if (d.joint && !d.joint.ready) return 'waiting';
    // 🔑 AN OPEN HIGH FRAUD FLAG HOLDS ONLY THE PULL (backend utils/fraudFlagPlacement, Jesse 2026-10-26): authorized,
    // and the borrower sees "we're confirming a few details" until staff resolve it — never the word fraud.
    if (d.pullHeld && !hasStep7Work(d, thisStep)) return 'confirming';
    return hasStep7Work(d, thisStep) ? 'conditions' : 'pulling';
  }
  return 'authorize';
}
